import { inject, Injectable, OnDestroy } from '@angular/core';
import { combineLatestWith, map, ReplaySubject, shareReplay, switchMap } from 'rxjs';
import { TreatmentClient } from '../../../core/client/treatment-client';
import { TreatmentRevisionDetail } from '../../../core/model/treatment-revision.model';
import { TreatmentDesign } from '../../../core/model/treatment-design.model';
import { TreatmentFileKind } from '../../../core/model/treatment-file.model';
import { UserStore } from '../../../core/store/user.store';
import { Member } from '../../../core/model/member.model';
import { sortBy } from 'lodash-es';
import { TreatmentTypeOriginalId } from '../../../core/model/treatmentType';

@Injectable()
export class TreatmentDesignStore implements OnDestroy {
    private readonly _treatmentId$ = new ReplaySubject<string>(1);
    private readonly _designer$ = new ReplaySubject<Member | null | undefined>(1);
    private readonly _activeRevision$ = new ReplaySubject<TreatmentRevisionDetail | null | undefined>(1);

    private readonly _treatmentClient = inject(TreatmentClient);
    private readonly _userStore = inject(UserStore);

    protected readonly user$ = this._userStore.currentUser$.pipe(shareReplay(1));

    readonly designs$ = this._treatmentId$.pipe(
        switchMap(treatmentId => this._treatmentClient.listDesigns(treatmentId)),
        shareReplay(1)
    );
    readonly activeDesign$ = this.designs$.pipe(
        combineLatestWith(this._activeRevision$),
        map(([designs, activeRevision]) =>
            this.mapDesign(
                activeRevision
                    ? designs.find(d => d.revisionId === activeRevision?.id)!
                    : designs.find(d => d.revisionId == null)
            )
        ),
        shareReplay(1)
    );
    readonly isAssignedDesigner$ = this.user$.pipe(
        combineLatestWith(this.activeDesign$, this._designer$),
        map(([u, , designer]) => designer != null && u.id === designer.id)
    );
    readonly isAssignedReviewer$ = this.user$.pipe(
        combineLatestWith(this.activeDesign$),
        map(([u, d]) => d != null && u.id === d.review?.reviewerId)
    );

    get treatmentIdSource() {
        return this._treatmentId$;
    }

    ngOnDestroy() {
        this._designer$.complete();
        this._treatmentId$.complete();
        this._activeRevision$.complete();
    }

    updateTreatmentId(value: string) {
        this._treatmentId$.next(value);
    }

    updateActiveRevision(value?: TreatmentRevisionDetail | null) {
        this._activeRevision$.next(value);
    }

    updateDesigner(value?: Member | null) {
        this._designer$.next(value);
    }

    private mapDesign(design?: TreatmentDesign | null) {
        if (!design) return null;
        const { files } = design;
        design.files = (
            [TreatmentTypeOriginalId.ClearAligner, TreatmentTypeOriginalId.SmileCorrect].includes(
                design.treatmentTypeOriginalId ?? -1
            )
                ? sortBy(files, f => f.friendlyName)
                : files
        ).sort(a => (a.fileKind === TreatmentFileKind.Engineering ? 1 : 0));
        return design;
    }
}
